import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaMedal, FaSort, FaSearch, FaTrophy } from "react-icons/fa";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { CompactMobileTable } from "@/components/ui/compact-mobile-table";
import { MobileTable } from "@/components/ui/mobile-table";
import { useMediaQuery } from "@/hooks/use-media-query";
import { filterMatchesByTime, TimeFilter } from "@/utils/matchTimeFilter";
import { EnhancedCard } from "@/components/ui/enhanced-card";
import {
  computeEloRatings,
  calculateBestWinStreak,
  calculateCurrentStreakWithSign,
} from "@/utils/playerStats";

// Define a type for a completed World Cup Run
interface CompletedWorldCupRun {
  date: Date; // Date when the World Cup was won (date of the last knockout match)
}

// Calculate World Cup Run count for a player
const calculateWorldCupRunCount = (matches: any[], playerId: number): number => {
  // Get matches where this player participated, sorted by date (newest first)
  const playerMatches = matches
    .filter(match => (match.teamA?.includes(playerId) || match.teamB?.includes(playerId)))
    .sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB.getTime() - dateA.getTime(); // Sort newest first
    });

  // If no matches, return 0
  if (playerMatches.length === 0) return 0;

  // Helper function to check if player won a match
  const didPlayerWin = (match: any): boolean => {
    const isInTeamA = match.teamA?.includes(playerId);
    return (isInTeamA && match.winner === 'A') || (!isInTeamA && match.winner === 'B');
  };

  // Helper function to check if match was a draw
  const isMatchDraw = (match: any): boolean => {
    return match.winner === 'Draw';
  };

  // Helper function to check if a group stage is valid (at least 2 wins, or 1 win and 1 draw)
  const isValidGroupStage = (matches: any[]): boolean => {
    if (matches.length !== 3) return false;

    let wins = 0;
    let draws = 0;

    matches.forEach(match => {
      if (didPlayerWin(match)) wins++;
      else if (isMatchDraw(match)) draws++;
    });

    // Valid if: 2+ wins OR 1 win + 1+ draws
    return wins >= 2 || (wins === 1 && draws >= 1);
  };

  // Track World Cup run count
  let worldCupRunCount = 0;

  // Create a chronologically ordered copy of the matches (oldest first)
  const chronologicalMatches = [...playerMatches].reverse();

  // Scan for completed World Cup runs
  let i = 0;
  while (i <= chronologicalMatches.length - 7) { // Need at least 7 matches for a complete run
    // Check if we have a valid group stage (3 consecutive matches)
    const potentialGroupStage = chronologicalMatches.slice(i, i + 3);

    if (isValidGroupStage(potentialGroupStage)) {
      // Found a valid group stage, now check for knockout matches
      const knockoutMatches = chronologicalMatches.slice(i + 3, i + 7);

      // Check if all knockout matches were wins
      const allKnockoutWins = knockoutMatches.every(match => didPlayerWin(match));

      if (allKnockoutWins) {
        // Found a valid World Cup run!
        worldCupRunCount++;

        // Skip this entire run
        i += 7;
        continue;
      }
    }

    // Move to the next potential starting point
    i++;
  }

  return worldCupRunCount;
};

// Calculate current win streak for a player (consecutive wins from most recent matches)
const calculateCurrentWinStreak = (matches: any[], playerId: number): number => {
  // Get matches where this player participated, sorted by date (newest first)
  const playerMatches = matches
    .filter(match => (match.teamA?.includes(playerId) || match.teamB?.includes(playerId)))
    .sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB.getTime() - dateA.getTime(); // Sort newest first
    });

  if (playerMatches.length === 0) return 0;

  let currentStreak = 0;

  // Count consecutive wins from the most recent matches
  for (let i = 0; i < playerMatches.length; i++) {
    const match = playerMatches[i];
    const isInTeamA = match.teamA?.includes(playerId);
    const isWinner = (isInTeamA && match.winner === 'A') || (!isInTeamA && match.winner === 'B');

    if (isWinner) {
      currentStreak++;
    } else {
      // Stop counting when we hit a loss or draw
      break;
    }
  }

  return currentStreak;
};

// Combined type for Leaderboard
interface LeaderboardPlayer {
  id: number;
  name: string;
  skills: number;
  effort: number;
  stamina: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  winRate: number;
  winStreak: number;
  bestStreak: number;
  elo: number;
  streakIsWin: boolean;
  worldCupRuns: number;
}

type SortField =
  | "name"
  | "played"
  | "winRate"
  | "winStreak"
  | "bestStreak"
  | "elo"
  | "wins"
  | "draws"
  | "losses"
  | "worldCupRuns";

const WIN_RATE_MIN_GAMES = 5;

interface DashboardLeaderboardProps {
  players: any[];
  matches: any[];
}

const DashboardLeaderboard = ({ players, matches }: DashboardLeaderboardProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [minGames, setMinGames] = useState(WIN_RATE_MIN_GAMES);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("winRate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Calculate maximum games played for slider
  const maxGamesPlayed = useMemo(() => {
    if (matches.length === 0) return 10;
    const playerGameCounts = new Map<number, number>();

    matches.forEach(match => {
      [...match.teamA, ...match.teamB].forEach(playerId => {
        playerGameCounts.set(playerId, (playerGameCounts.get(playerId) || 0) + 1);
      });
    });

    return Math.max(...Array.from(playerGameCounts.values()), 10);
  }, [matches]);

  const timeFilteredMatches = useMemo(
    () => filterMatchesByTime(matches, timeFilter),
    [matches, timeFilter]
  );

  // Calculate leaderboard data from players and matches
  const leaderboardData = useMemo(() => {
    const filteredMatches = yearFilter === 'all'
      ? timeFilteredMatches
      : timeFilteredMatches.filter(match => {
          const matchDate = match.date instanceof Date
            ? match.date
            : new Date(match.date || match.match_date);
          return matchDate.getFullYear() === parseInt(yearFilter);
        });

    const normalizedMatches = filteredMatches.map((m) => ({
      teama: m.teamA ?? m.teama,
      teamb: m.teamB ?? m.teamb,
      winner: m.winner,
      match_date: (m.date instanceof Date ? m.date : new Date(m.date || m.match_date)).toISOString(),
    }));

    const eloMap = computeEloRatings(
      players.map((p) => p.id),
      normalizedMatches
    );

    return players.map(player => {
      const playerMatches = filteredMatches.filter(match =>
        (match.teamA && match.teamA.includes(player.id)) ||
        (match.teamB && match.teamB.includes(player.id))
      );

      const gamesPlayed = playerMatches.length;
      const wins = playerMatches.filter(match => {
        const inTeamA = match.teamA && match.teamA.includes(player.id);
        return (inTeamA && match.winner === 'A') || (!inTeamA && match.winner === 'B');
      }).length;
      const draws = playerMatches.filter(match => match.winner === 'Draw').length;
      const losses = gamesPlayed - wins - draws;

      // Calculate World Cup Runs won
      const worldCupRuns = calculateWorldCupRunCount(filteredMatches, player.id);

      const winStreakInfo = calculateCurrentStreakWithSign(normalizedMatches, player.id);
      const bestStreak = calculateBestWinStreak(normalizedMatches, player.id);

      return {
        id: player.id,
        name: player.name,
        skills: player.skills,
        effort: player.effort,
        stamina: player.stamina,
        played: gamesPlayed,
        wins,
        draws,
        losses,
        winRate: gamesPlayed > 0 ? (wins / gamesPlayed) * 100 : 0,
        winStreak: winStreakInfo.streak,
        streakIsWin: winStreakInfo.isWinStreak,
        bestStreak,
        elo: eloMap.get(player.id) ?? 1000,
        worldCupRuns
      };
    });
  }, [players, timeFilteredMatches, yearFilter]);

  // Filter and sort the calculated leaderboard data
  const filteredAndSortedPlayers = useMemo(() => {
    return [...leaderboardData]
      .filter(
        (player) =>
          player.name.toLowerCase().includes(search.toLowerCase()) &&
          player.played >= minGames &&
          (sortField !== "winRate" || player.played >= WIN_RATE_MIN_GAMES)
      )
      .sort((a, b) => {
        let comparison = 0;
        if (sortField === "name") {
          comparison = a.name.localeCompare(b.name);
        } else {
          comparison = (a[sortField] ?? 0) - (b[sortField] ?? 0);
        }
        return sortDirection === "asc" ? comparison : -comparison;
      });
  }, [leaderboardData, search, minGames, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (field !== sortField) return null;
    return (
      <FaSort className={`ml-1 h-4 w-4 inline ${sortDirection === "asc" ? "rotate-180" : ""}`} />
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <EnhancedCard>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <FaMedal className="h-5 w-5 text-soccer-primary" />
            <div className="font-semibold text-lg text-foreground">{t('nav.leaderboard')}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative w-full sm:w-80">
              <FaSearch className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('players.searchPlayers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
              <div className="flex items-center gap-6 w-full max-w-2xl">
                <div className="text-sm text-muted-foreground whitespace-nowrap min-w-fit">
                  {t('leaderboard.minGames', 'Min. games')}: <span className="font-medium text-foreground">{minGames}</span>
                </div>
                <div className="flex-1 px-4 min-w-[200px] sm:min-w-[300px]">
                  <Slider
                    value={[minGames]}
                    onValueChange={(value) => setMinGames(value[0])}
                    max={maxGamesPlayed}
                    min={0}
                    step={1}
                    className="w-full h-8"
                    aria-label={`Minimum games filter, current value: ${minGames}, maximum: ${maxGamesPlayed}`}
                  />
                </div>
              </div>

              <span className="text-sm whitespace-nowrap">{t('stats.timeFilter', 'Period')}:</span>
              <Select value={timeFilter} onValueChange={(v) => setTimeFilter(v as TimeFilter)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('stats.allTime', 'All time')}</SelectItem>
                  <SelectItem value="last10">{t('stats.last10', 'Last 10 matches')}</SelectItem>
                  <SelectItem value="thisMonth">{t('stats.thisMonth', 'This month')}</SelectItem>
                </SelectContent>
              </Select>

              <span className="text-sm whitespace-nowrap">{t('leaderboard.year', 'Year:')}:</span>
              <Select
                value={yearFilter}
                onValueChange={setYearFilter}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder={t('leaderboard.yearPlaceholder', 'Year')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('leaderboard.allYears', 'All Years')}</SelectItem>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </EnhancedCard>

      {/* Leaderboard Table Card */}
      <EnhancedCard>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FaTrophy className="h-5 w-5 text-soccer-primary" />
              <div className="font-semibold text-lg text-foreground">{t('leaderboard.rankings', 'Rankings')}</div>
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredAndSortedPlayers.length} {t('players.players', 'players')}
            </div>
          </div>

          {isMobile ? (
            <MobileTable
              data={filteredAndSortedPlayers}
              columns={[
                {
                  header: t('leaderboard.rank', '#'),
                  accessorKey: 'id' as keyof LeaderboardPlayer,
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => {
                    const idx = filteredAndSortedPlayers.findIndex(p => p.id === player.id);
                    return (
                      <div className="flex items-center justify-center">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-soccer-primary dark:border-soccer-primary bg-transparent">
                          <span className="text-soccer-primary dark:text-soccer-primary text-xs font-medium">{idx + 1}</span>
                        </div>
                      </div>
                    );
                  },
                  meta: { align: "center", width: "10%" }
                },
                {
                  header: t('players.name'),
                  accessorKey: 'name',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <span className="text-xs font-medium truncate max-w-[60px] block">{player.name}</span>
                  ),
                  meta: { align: "left", width: "20%" }
                },
                {
                  header: t('leaderboard.w', 'W'),
                  accessorKey: 'wins',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <span className="text-green-600 font-medium text-xs">{player.wins}</span>
                  ),
                  meta: { align: "center", width: "8%" }
                },
                {
                  header: t('leaderboard.d', 'D'),
                  accessorKey: 'draws',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <span className="text-gray-500 font-medium text-xs">{player.draws}</span>
                  ),
                  meta: { align: "center", width: "8%" }
                },
                {
                  header: t('leaderboard.l', 'L'),
                  accessorKey: 'losses',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <span className="text-red-600 font-medium text-xs">{player.losses}</span>
                  ),
                  meta: { align: "center", width: "8%" }
                },
                {
                  header: t('leaderboard.winRate', 'Win%'),
                  accessorKey: 'winRate',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <span className={`text-xs font-medium ${player.winRate >= 70 ? "text-green-600" : player.winRate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                      {player.winRate.toFixed(0)}%
                    </span>
                  ),
                  meta: { align: "center", width: "15%" }
                },
                {
                  header: t('leaderboard.winStreak', 'Streak'),
                  accessorKey: 'winStreak',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <div className="flex items-center justify-center">
                      {player.winStreak > 0 ? (
                        <div className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100 rounded-full px-1.5 py-0.5 text-xs font-medium">
                          {player.winStreak}
                          {player.streakIsWin ? "🔥" : "❄️"}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  ),
                  meta: { align: "center", width: "15%" }
                },
                {
                  header: t('leaderboard.wc', 'WC'),
                  accessorKey: 'worldCupRuns',
                  priority: "high",
                  cell: (player: LeaderboardPlayer) => (
                    <div className="flex items-center justify-center">
                      {player.worldCupRuns > 0 ? (
                        <div className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100 rounded-full px-1.5 py-0.5 text-xs font-medium">
                          {player.worldCupRuns}x 🏆
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  ),
                  meta: { align: "center", width: "15%" }
                }
              ]}
              keyExtractor={(player) => player.id}
              isLoading={false}
              rowClassName={(player) => ""}
            />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px] text-center">{t('leaderboard.rank', 'Rank')}</TableHead>
                    <TableHead>
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("name")}>
                        {t('players.name')} {renderSortIcon("name")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("wins")}>
                        {t('leaderboard.w', 'W')} {renderSortIcon("wins")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("draws")}>
                        {t('leaderboard.d', 'D')} {renderSortIcon("draws")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("losses")}>
                        {t('leaderboard.l', 'L')} {renderSortIcon("losses")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("played")}>
                        {t('leaderboard.p', 'P')} {renderSortIcon("played")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("winRate")}>
                        {t('players.winRate')} {renderSortIcon("winRate")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("winStreak")}>
                        {t('leaderboard.winStreak', 'Streak')} {renderSortIcon("winStreak")}
                      </Button>
                    </TableHead>
                    <TableHead className="text-center">
                      <Button variant="ghost" className="p-0 font-bold hover:bg-transparent" onClick={() => handleSort("worldCupRuns")}>
                        {t('leaderboard.wc', 'WC')} {renderSortIcon("worldCupRuns")}
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPlayers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="h-24 text-center">
                        {t('leaderboard.noPlayers', 'No players found')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAndSortedPlayers.map((player, index) => (
                      <TableRow key={player.id}>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-soccer-primary dark:border-soccer-primary bg-transparent">
                              <span className="text-soccer-primary dark:text-soccer-primary text-xs font-medium">{index + 1}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{player.name}</TableCell>
                        <TableCell className="text-center">{player.wins}</TableCell>
                        <TableCell className="text-center">{player.draws}</TableCell>
                        <TableCell className="text-center">{player.losses}</TableCell>
                        <TableCell className="text-center">{player.played}</TableCell>
                        <TableCell className="text-center">{player.winRate.toFixed(1)}%</TableCell>
                        <TableCell className="text-center">
                          {player.winStreak > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100">
                              {player.winStreak}🔥
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {player.worldCupRuns > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                              {player.worldCupRuns}x 🏆
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </EnhancedCard>
    </div>
  );
};

export default DashboardLeaderboard;
